import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@qodo/design-system';
import { CONTACT_SUPPORT_URL } from 'consts/url';
import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { usePricingConfig } from 'hooks/billing/usePricingConfig';
import { useRecommendation } from 'hooks/billing/useRecommendation';
import { USAGE_MODEL_BANNER_DISMISS_METHODS } from 'services/mixpanel/consts';
import {
  DEFAULT_MIGRATION_WINDOW_DAYS,
  DEFAULT_TRIAL_DAYS,
  getMigrationDaysRemaining,
  isMigratedPaidUser,
} from 'utils/billing/conversion';
import { spawn } from 'utils/translations';
import { FeatureList } from '../ConversionPopup/parts/FeatureList';
import { PaymentActionConsent } from '../parts/PaymentActionConsent';
import { TrialBonusAlert } from '../parts/TrialBonusAlert';
import { TIER_SUBTITLE_KEYS } from './consts';
import { RecommendedPlanCard } from './parts/RecommendedPlanCard';
import { useMigrationWelcomePopup } from './useMigrationWelcomePopup';
import { formatTrialStartDate } from './utils';

const spawnPopup = spawn('billing.trial-welcome');

export const MigrationWelcomePopup = () => {
  const {
    isOpen,
    onSnooze,
    onSelectPlan,
    onContactSupport,
    isMigrating,
    moveFailed,
    showMoveConsent,
    onConfirmReverse,
  } = useMigrationWelcomePopup();
  const { data: accountInfo } = useAccountInfo();
  const cfg = usePricingConfig();
  const tier = accountInfo?.tier;
  const trial = accountInfo?.trial;
  const { data: recommendation } = useRecommendation();

  const isPaidCohort =
    isMigratedPaidUser(accountInfo) &&
    (getMigrationDaysRemaining(accountInfo?.migrationEndDate) ?? 0) > 0;
  const totalDays = isPaidCohort
    ? getMigrationDaysRemaining(accountInfo?.migrationEndDate) ??
      DEFAULT_MIGRATION_WINDOW_DAYS
    : trial?.totalDays ?? DEFAULT_TRIAL_DAYS;
  const trialStartDate = formatTrialStartDate(trial?.dayNumber ?? 1);
  const subtitleKey =
    tier && TIER_SUBTITLE_KEYS[tier as keyof typeof TIER_SUBTITLE_KEYS];

  const recommendedSku = useMemo(() => {
    if (!cfg.plans.length) return undefined;

    const ascending = [...cfg.plans].sort((a, b) => a.credits - b.credits);

    if (recommendation?.available && recommendation.recommendedPlan) {
      const target = recommendation.recommendedPlan.credits;
      const covering = ascending.find((plan) => plan.credits >= target);

      return (covering ?? ascending[ascending.length - 1]).sku;
    }

    return ascending[0].sku;
  }, [cfg.plans, recommendation]);

  const recommendedPlan = cfg.plans.find((p) => p.sku === recommendedSku);

  // Paid-cohort Move needs a resolved plan sku; keep the CTA disabled until the
  // ladder loads (else the click is a no-op) and while a Move is in flight.
  const ctaDisabled = (isPaidCohort && !recommendedPlan) || isMigrating;

  const subtitleNode = subtitleKey ? (
    <Trans
      i18nKey={`billing.trial-welcome.${subtitleKey}`}
      values={{ days: totalDays, date: trialStartDate }}
      components={{ bold: <span className="font-semibold" />, br: <br /> }}
    />
  ) : undefined;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) =>
        !open && onSnooze(USAGE_MODEL_BANNER_DISMISS_METHODS.CLOSE_X)
      }
    >
      <DialogContent
        data-testid="migration-welcome-popup"
        className="slide-in-from-right-[0px]! slide-in-from-bottom-[0px]! max-w-220! w-full p-10 gap-10"
        // A click outside the content (e.g. the GTM-injected cookie banner, which
        // lives outside the React root) must not dismiss the popup — only the
        // close-X or Esc. Mirrors the ConversionPopup backdrop guard.
        onPointerDownOutside={(event) => event.preventDefault()}
        // Radix's default auto-focus lands on the "Upgrade now" CTA, which then
        // shows a focus-visible ring that isn't in the design. Prevent it and let
        // focus fall to the Content container (tabIndex -1) instead: the modal
        // stays keyboard-accessible while the CTA shows no ring. Keyboard users
        // still get the ring when they Tab to the button.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="mb-4">
          {subtitleNode && (
            <DialogTitle className="text-h5! font-normal leading-[120%] max-w-[90%] tracking-normal text-text-general-neutral-mid">
              {subtitleNode}
            </DialogTitle>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-8">
          {showMoveConsent ? (
            <PaymentActionConsent
              onConfirm={onConfirmReverse}
              loading={isMigrating}
            />
          ) : (
            <div className="flex flex-col gap-10">
              <div className="flex flex-col overflow-hidden rounded-lg border border-border-interactive-emphasis-active">
                <div className="flex gap-6 flex-col p-6">
                  <p className="flex items-center text-sm text-text-general-neutral-subtle">
                    {spawnPopup('plan-section-audience')}
                  </p>
                  <div className="flex gap-6 items-center">
                    <div className="shrink-0 w-100">
                      {recommendedPlan ? (
                        <RecommendedPlanCard plan={recommendedPlan} />
                      ) : (
                        <Skeleton className="w-full h-35 rounded-lg" />
                      )}
                    </div>
                    <FeatureList
                      featuresClassName="grid-rows-3 gap-x-4"
                      propsClassName="flex-col gap-3.5"
                    />
                  </div>
                  <TrialBonusAlert
                    onCtaClick={() => onSelectPlan(recommendedPlan?.sku)}
                    variant="popup"
                    disabled={ctaDisabled}
                    loading={isMigrating}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {moveFailed && (
          <p
            role="alert"
            data-testid="migration-move-error"
            className="text-sm text-text-general-danger-mid"
          >
            {spawnPopup('move-error')}
          </p>
        )}

        <DialogFooter className="mt-4 gap-2 flex justify-between! items-center w-full">
          <p className="text-sm text-text-general-neutral-subtle">
            {spawnPopup('support-text')}{' '}
            <a
              href={CONTACT_SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              onClick={onContactSupport}
              className="underline text-text-action-emphasis-idle hover:text-text-action-emphasis-hover"
            >
              {spawnPopup('support-link')}
            </a>
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              onSnooze(USAGE_MODEL_BANNER_DISMISS_METHODS.OK_BUTTON)
            }
          >
            {spawnPopup('button-later')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
